<template>
  <!--
    版画主题海景背景（纯内联 SVG + CSS 动画，无图片/视频/canvas）
    - 仅 linocut 主题下挂载；fixed 铺底，pointer-events:none，不拦截点击
    - 动画全部走 transform/opacity（GPU 加速），页面失焦自动暂停
    - 移动端自动降低动画强度（prefers-reduced-motion + 媒体查询）
  -->
  <div class="sea-bg" :class="{ 'is-paused': paused }" :style="{ opacity: bgOpacity }">
    <svg
      class="sea-bg-svg"
      viewBox="0 0 1920 1080"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- 天空（泛黄纸张底） -->
      <rect width="1920" height="600" fill="#f3ecd9" />

      <!-- 太阳光芒组（缓慢旋转，周期 25s） -->
      <g class="sun-rays">
        <g stroke="#f2a938" stroke-width="3" stroke-linecap="round">
          <!-- 8 条放射短线，围绕太阳中心 (240, 560) -->
          <line x1="240" y1="440" x2="240" y2="400" />
          <line x1="160" y1="560" x2="120" y2="560" />
          <line x1="320" y1="560" x2="360" y2="560" />
          <line x1="185" y1="495" x2="155" y2="465" />
          <line x1="295" y1="495" x2="325" y2="465" />
          <line x1="185" y1="625" x2="155" y2="655" />
          <line x1="295" y1="625" x2="325" y2="655" />
          <line x1="240" y1="680" x2="240" y2="720" />
        </g>
      </g>
      <!-- 太阳圆盘 -->
      <circle cx="240" cy="560" r="80" fill="#f2a938" stroke="#222" stroke-width="3" />

      <!-- 大云朵组（左，漂移周期 40s） -->
      <g class="cloud-big">
        <g fill="#fffdf8" stroke="#222" stroke-width="3">
          <circle cx="820" cy="320" r="48" />
          <circle cx="880" cy="296" r="54" />
          <circle cx="946" cy="308" r="50" />
          <circle cx="1002" cy="326" r="44" />
          <circle cx="1058" cy="320" r="42" />
        </g>
        <!-- 云朵内部排线纹理（版画肌理） -->
        <g stroke="#c9c2b0" stroke-width="1.5" fill="none">
          <line x1="790" y1="318" x2="1080" y2="318" />
          <line x1="798" y1="332" x2="1070" y2="332" />
          <line x1="808" y1="346" x2="1060" y2="346" />
          <line x1="820" y1="360" x2="1048" y2="360" />
        </g>
      </g>

      <!-- 小云朵组（右，漂移周期 32s） -->
      <g class="cloud-small">
        <g fill="#fffdf8" stroke="#222" stroke-width="3">
          <circle cx="1320" cy="220" r="44" />
          <circle cx="1380" cy="200" r="50" />
          <circle cx="1450" cy="212" r="46" />
          <circle cx="1510" cy="228" r="40" />
        </g>
        <!-- 排线纹理 -->
        <g stroke="#c9c2b0" stroke-width="1.5" fill="none">
          <line x1="1292" y1="218" x2="1530" y2="218" />
          <line x1="1300" y1="232" x2="1522" y2="232" />
          <line x1="1312" y1="246" x2="1510" y2="246" />
        </g>
      </g>

      <!-- 海平线 -->
      <line x1="0" y1="600" x2="1920" y2="600" stroke="#222" stroke-width="3" />

      <!-- 海面 -->
      <rect y="600" width="1920" height="480" fill="#3688a8" />

      <!-- 海面手绘波浪线条（每条错位起伏，错开时间模拟流动） -->
      <g class="waves" stroke="#ffffff" stroke-width="2.5" fill="none">
        <!-- 用 animateTransform 实现起伏；每条 delay 错开 -->
        <path class="wave" style="--d: 0s"   d="M0,660 Q150,640 300,670 T600,660 T900,670 T1200,650 T1500,680 T1920,665" />
        <path class="wave" style="--d: 0.6s" d="M0,720 Q150,700 300,730 T600,720 T900,730 T1200,710 T1500,740 T1920,725" />
        <path class="wave" style="--d: 1.2s" d="M0,790 Q150,770 300,800 T600,790 T900,800 T1200,780 T1500,810 T1920,795" />
        <path class="wave" style="--d: 1.8s" d="M0,870 Q150,850 300,880 T600,870 T900,880 T1200,860 T1500,890 T1920,875" />
        <path class="wave" style="--d: 2.4s" d="M0,950 Q150,930 300,960 T600,950 T900,960 T1200,940 T1500,970 T1920,955" />
      </g>

      <!-- 太阳水面反光光斑（缓慢闪烁明暗） -->
      <g class="sun-glints" fill="#ffe290">
        <rect class="glint" style="--d: 0s"   x="200" y="612" width="40" height="4" />
        <rect class="glint" style="--d: 0.4s" x="212" y="632" width="32" height="4" />
        <rect class="glint" style="--d: 0.8s" x="196" y="656" width="44" height="4" />
        <rect class="glint" style="--d: 1.2s" x="208" y="680" width="28" height="4" />
        <rect class="glint" style="--d: 1.6s" x="200" y="706" width="38" height="4" />
        <rect class="glint" style="--d: 2.0s" x="214" y="734" width="30" height="4" />
        <rect class="glint" style="--d: 2.4s" x="204" y="762" width="36" height="4" />
      </g>

      <!-- 小帆船（右→左漂移 80s + 随浪轻晃） -->
      <g class="sailboat">
        <g stroke="#222" stroke-width="2.5" stroke-linejoin="round">
          <!-- 桅杆 -->
          <line x1="0" y1="0" x2="0" y2="-44" stroke-width="3" />
          <!-- 帆 -->
          <path d="M0,-42 L30,-8 L0,-8 Z" fill="#fffdf8" />
          <!-- 船身 -->
          <path d="M-30,0 L30,0 L22,14 L-22,14 Z" fill="#c46233" />
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'SeaBackground',
  props: {
    /** 背景透明度 0~0.3 */
    bgOpacity: { type: Number, default: 0.12 },
    /** 页面失焦时传入 true 暂停动画 */
    paused: { type: Boolean, default: false }
  }
}
</script>

<style scoped>
.sea-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  overflow: hidden;
  background-color: #f3ecd9; /* 泛黄纸张底兜底 */
}

.sea-bg-svg {
  width: 100%;
  height: 100%;
  display: block;
}

/* 页面失焦 / 减少动画偏好 → 全部暂停 */
.sea-bg.is-paused .sun-rays,
.sea-bg.is-paused .cloud-big,
.sea-bg.is-paused .cloud-small,
.sea-bg.is-paused .wave,
.sea-bg.is-paused .glint,
.sea-bg.is-paused .sailboat {
  animation-play-state: paused !important;
}

/* ===== ① 太阳光芒：缓慢旋转，周期 25s（连续一周，幅度均匀不刺眼） ===== */
.sun-rays {
  transform-origin: 240px 560px;
  animation: sun-rays-rotate 25s linear infinite;
  will-change: transform;
}
@keyframes sun-rays-rotate {
  to { transform: rotate(360deg); }
}

/* ===== ② 云朵：左右往复漂移；大云 40s，小云 32s ===== */
.cloud-big {
  animation: cloud-drift-1 40s ease-in-out infinite;
  will-change: transform;
}
.cloud-small {
  animation: cloud-drift-2 32s ease-in-out infinite;
  will-change: transform;
}
@keyframes cloud-drift-1 {
  0%, 100% { transform: translateX(-24px); }
  50%      { transform: translateX(24px); }
}
@keyframes cloud-drift-2 {
  0%, 100% { transform: translateX(20px); }
  50%      { transform: translateX(-20px); }
}

/* ===== ③ 海面波浪：每条上下轻微起伏，错开 delay 模拟流动 ===== */
.wave {
  animation: wave-undulate 5s ease-in-out infinite;
  animation-delay: var(--d, 0s);
  will-change: transform;
}
@keyframes wave-undulate {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-5px); }
}

/* ===== 水面光斑：明暗闪烁 ===== */
.glint {
  animation: glint-flicker 3.5s ease-in-out infinite;
  animation-delay: var(--d, 0s);
  will-change: opacity;
}
@keyframes glint-flicker {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}

/* ===== ④ 帆船：右→左缓慢漂移 80s + 随浪轻晃 ===== */
.sailboat {
  /* 初始定位到海面右侧，由 transform 控制漂移 */
  transform-origin: center;
  animation: sailboat-drift 80s linear infinite, sailboat-bob 4s ease-in-out infinite;
  will-change: transform;
}
/* 帆船基准位置：海面右侧偏下 */
:deep(.sailboat) {
  transform-box: fill-box;
}
@keyframes sailboat-drift {
  0%   { transform: translate(1500px, 640px); }
  100% { transform: translate(-260px, 640px); }
}
@keyframes sailboat-bob {
  0%, 100% { translate: 0 0; }
  50%      { translate: 0 -3px; }
}

/* ===== 移动端降级：减少波浪动画数量、降低频率 ===== */
@media (max-width: 768px) {
  .wave:nth-child(n+4) { display: none; }          /* 只保留前 3 条波浪 */
  .glint:nth-child(n+4) { display: none; }         /* 只保留前 3 个光斑 */
  .sun-rays { animation-duration: 60s; }           /* 光芒转得更慢 */
  .wave { animation-duration: 8s; }                /* 波浪起伏变慢 */
  .sailboat { animation-duration: 140s, 6s; }      /* 帆船漂移变慢 */
}

/* 系统级减少动画偏好：直接关闭所有动画 */
@media (prefers-reduced-motion: reduce) {
  .sun-rays,
  .cloud-big,
  .cloud-small,
  .wave,
  .glint,
  .sailboat {
    animation: none !important;
  }
}
</style>
